/**
 * /me - minimal account placeholder for Day-1 (post-OAuth landing).
 *
 * Phase 2 will replace with full account page (profile, sessions, billing,
 * data export per PDPA Article 14). For Day-1 we render the decoded JWT
 * profile (avatar + name + email) so the user has visual confirmation
 * the OAuth round-trip succeeded (per BUG-2026-05-05-04 fix). Profile
 * is best-effort UX data; server verifies token per request.
 */

import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../use-auth';
import { useLang } from '../use-lang';

export function MeRoute() {
  const navigate = useNavigate();
  const { isAuthenticated, profile, logout } = useAuth();
  const { lang } = useLang();

  if (!isAuthenticated) {
    const t = {
      heading: lang === 'vi' ? 'Bạn chưa đăng nhập' : 'You are not signed in',
      body: lang === 'vi' ? 'Vui lòng đăng nhập để tiếp tục.' : 'Please sign in to continue.',
      cta: lang === 'vi' ? 'Đăng nhập' : 'Sign in',
    };
    return (
      <main style={{ maxWidth: 460, margin: '64px auto', padding: '0 16px', textAlign: 'center' }}>
        <h1>{t.heading}</h1>
        <p style={{ color: 'var(--color-muted)' }}>{t.body}</p>
        <Link to="/auth/login" className="cta-primary" style={{ marginTop: 16 }}>
          {t.cta}
        </Link>
      </main>
    );
  }

  const t = {
    heading: lang === 'vi' ? 'Tài khoản' : 'Account',
    phase2: lang === 'vi'
      ? 'Bạn đã đăng nhập thành công. Trang tài khoản đầy đủ sẽ có ở Phase 2.'
      : 'You are signed in successfully. The full account page is coming in Phase 2.',
    logout: lang === 'vi' ? 'Đăng xuất' : 'Sign out',
    fallbackName: lang === 'vi' ? 'Người dùng' : 'User',
    fallbackProfile: lang === 'vi'
      ? 'Không đọc được thông tin hồ sơ từ token. Bạn vẫn đăng nhập thành công.'
      : 'Could not decode profile from token. You are still signed in.',
  };

  const displayName = profile?.name ?? t.fallbackName;
  const initial = (profile?.name?.[0] ?? profile?.email?.[0] ?? '?').toUpperCase();

  return (
    <main style={{ maxWidth: 640, margin: '64px auto', padding: '0 16px' }}>
      <h1>{t.heading}</h1>

      {profile ? (
        <section
          aria-label={t.heading}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 16,
            padding: 20,
            border: '1px solid var(--asklaw-border)',
            borderRadius: 12,
            background: 'var(--color-card)',
            maxWidth: 560,
            marginTop: 16,
          }}
        >
          {profile.picture ? (
            <img
              src={profile.picture}
              alt=""
              width={56}
              height={56}
              referrerPolicy="no-referrer"
              style={{
                borderRadius: '50%',
                display: 'block',
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              aria-hidden
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--asklaw-orange)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--font-sans)',
                fontWeight: 700,
                fontSize: 22,
                flexShrink: 0,
              }}
            >
              {initial}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <strong style={{ fontFamily: 'var(--font-sans)', fontSize: 16 }}>{displayName}</strong>
            {profile.email ? (
              <span
                style={{
                  color: 'var(--color-muted)',
                  fontSize: 14,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {profile.email}
              </span>
            ) : null}
          </div>
        </section>
      ) : (
        <div
          role="status"
          className="banner"
          style={{ marginTop: 16, fontSize: 14 }}
        >
          {t.fallbackProfile}
        </div>
      )}

      <p style={{ color: 'var(--color-muted)', marginTop: 24 }}>{t.phase2}</p>

      <button
        type="button"
        onClick={() => {
          logout();
          navigate('/');
        }}
        style={{
          marginTop: 24,
          padding: '10px 16px',
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        {t.logout}
      </button>
    </main>
  );
}
